import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, CreditCard, FileText, Loader2, XCircle } from 'lucide-react';
import {
  confirmCheckout,
  createInvoiceCheckout,
  fetchPayableInvoices,
} from '../api/candidate';
import { formatEuro } from '../lib/format';
import dayjs from '../lib/dates';

/** Bannière de retour depuis Stripe (?payment=success|cancelled). */
function usePaymentReturn(onSettled: () => void) {
  const [banner, setBanner] = useState<'success' | 'cancelled' | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const state = params.get('payment');
    const sessionId = params.get('session_id');
    if (state === 'success' || state === 'cancelled') {
      setBanner(state);
      params.delete('payment');
      params.delete('invoice');
      params.delete('session_id');
      const qs = params.toString();
      window.history.replaceState(
        {},
        '',
        window.location.pathname + (qs ? `?${qs}` : ''),
      );
      if (state === 'success') {
        // Réconciliation immédiate au retour : on confirme la session Stripe
        // (officialise la facture sans attendre le webhook), puis on rafraîchit.
        if (sessionId) {
          confirmCheckout(sessionId)
            .catch(() => undefined)
            .finally(onSettled);
        } else {
          onSettled();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return { banner, dismiss: () => setBanner(null) };
}

/**
 * Factures à régler par l'élève + règlement en ligne (Stripe Checkout).
 * Affiché sur le tableau de bord (toujours visible) et dans « Mes paiements ».
 */
export default function PayableInvoices() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['candidate', 'payable-invoices'],
    queryFn: fetchPayableInvoices,
  });

  const { banner, dismiss } = usePaymentReturn(() => {
    queryClient.invalidateQueries({ queryKey: ['candidate', 'payable-invoices'] });
    queryClient.invalidateQueries({ queryKey: ['candidate', 'payments'] });
  });

  const [pendingId, setPendingId] = useState<string | null>(null);
  const checkout = useMutation({
    mutationFn: (invoiceId: string) => createInvoiceCheckout(invoiceId),
    onMutate: (invoiceId) => setPendingId(invoiceId),
    onSuccess: ({ url }) => {
      window.location.href = url; // redirection vers Stripe Checkout
    },
    onError: () => setPendingId(null),
  });

  const invoices = data?.invoices ?? [];
  const paymentEnabled = data?.paymentEnabled ?? false;

  return (
    <>
      {banner && (
        <div
          className={`rounded-2xl border p-4 flex items-center gap-3 ${
            banner === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-amber-50 border-amber-200 text-amber-800'
          }`}
        >
          {banner === 'success' ? <CheckCircle2 size={18} /> : <XCircle size={18} />}
          <span className="text-sm font-medium">
            {banner === 'success'
              ? 'Paiement effectué, merci ! Votre auto-école a été créditée.'
              : 'Paiement annulé — aucune somme n’a été prélevée.'}
          </span>
          <button type="button" onClick={dismiss} className="ml-auto text-xs underline">
            Fermer
          </button>
        </div>
      )}

      {(isLoading || invoices.length > 0) && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-slate-900">Factures à régler</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            Payez vos factures en ligne par carte bancaire, en toute sécurité.
          </p>

          {isLoading ? (
            <div className="flex items-center text-slate-500 text-sm py-2">
              <Loader2 className="animate-spin mr-2" size={16} /> Chargement…
            </div>
          ) : !paymentEnabled ? (
            <p className="text-sm text-slate-500">
              Le paiement en ligne n’est pas encore activé par votre auto-école.
            </p>
          ) : (
            <div className="divide-y divide-slate-100">
              {invoices.map((inv) => (
                <div key={inv.id} className="flex items-center gap-4 py-3.5 first:pt-0">
                  <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                    <FileText className="text-indigo-600" size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">
                      {inv.title}
                    </div>
                    <div className="text-xs text-slate-500">
                      {inv.number} · {dayjs(inv.issueDate).format('DD/MM/YYYY')}
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-slate-900 flex-shrink-0">
                    {formatEuro(inv.totalTtc)}
                  </div>
                  <button
                    type="button"
                    onClick={() => checkout.mutate(inv.id)}
                    disabled={pendingId === inv.id}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-indigo-600 rounded-lg px-3 py-2 hover:bg-indigo-700 disabled:opacity-60 flex-shrink-0"
                  >
                    {pendingId === inv.id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <CreditCard size={14} />
                    )}
                    Régler la facture
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
